import React, { useState, useEffect, useRef } from 'react';
import { Textarea } from '@/components/ui/textarea';
import api from '../utils/api';

/**
 * MentionInput - Textarea con funcionalidad de menciones @usuario
 * 
 * @param {string} value - Valor actual del textarea
 * @param {function} onChange - Callback cuando cambia el texto
 * @param {function} onMentionsChange - Callback cuando se añaden/quitan menciones [{id, name}]
 * @param {string} placeholder - Placeholder del textarea
 * @param {number} rows - Número de filas
 * @param {string} className - Clases CSS adicionales
 */
const MentionInput = ({ 
  value = '', 
  onChange, 
  onMentionsChange,
  placeholder = 'Escribe aquí... Usa @ para mencionar',
  rows = 4,
  className = '',
  ...props 
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);

  // Cargar usuarios mencionables al montar
  useEffect(() => {
    fetchMencionables();
  }, []);

  // Filtrar usuarios cuando cambia la búsqueda
  useEffect(() => {
    if (searchQuery) {
      const filtered = users.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered.slice(0, 8)); // Máximo 8 sugerencias
    } else {
      setFilteredUsers(users.slice(0, 8));
    }
  }, [searchQuery, users]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchMencionables = async () => {
    try {
      const response = await api.get('/users/mencionables');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error al cargar usuarios mencionables:', error);
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    // Detectar si se escribió @
    const textBeforeCursor = newValue.slice(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      // Verificar si estamos escribiendo una mención (después de @ y antes de un espacio)
      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
      const hasSpaceAfterAt = textAfterAt.includes(' ');
      
      if (!hasSpaceAfterAt) {
        setSearchQuery(textAfterAt);
        setShowDropdown(true);
        
        // Calcular posición del dropdown
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          const rect = textarea.getBoundingClientRect();
          // Posición aproximada
          setDropdownPosition({
            top: rect.height + 4,
            left: 0
          });
        }
      } else {
        setShowDropdown(false);
      }
    } else {
      setShowDropdown(false);
    }

    onChange?.(newValue);
  };

  const handleSelectUser = (user) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = value.slice(cursorPosition);
    
    // Reemplazar @query con @nombre
    const newText = value.slice(0, lastAtIndex) + `@${user.name} ` + textAfterCursor;
    
    // Añadir a la lista de menciones si no está ya
    const newMentions = [...mentions];
    if (!newMentions.find(m => m.id === user.id)) {
      newMentions.push({ id: user.id, name: user.name });
      setMentions(newMentions);
      onMentionsChange?.(newMentions);
    }
    
    onChange?.(newText);
    setShowDropdown(false);
    
    // Enfocar de nuevo el textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (showDropdown && filteredUsers.length > 0) {
      if (e.key === 'Escape') {
        setShowDropdown(false);
        e.preventDefault();
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        // Seleccionar el primer usuario
        handleSelectUser(filteredUsers[0]);
        e.preventDefault();
      }
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className={`resize-none ${className}`}
        {...props}
      />
      
      {/* Dropdown de sugerencias */}
      {showDropdown && filteredUsers.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          style={{ top: dropdownPosition.top }}
        >
          <div className="p-2 text-xs text-slate-500 border-b border-slate-100 bg-slate-50">
            Mencionar a:
          </div>
          {filteredUsers.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => handleSelectUser(user)}
              className="w-full text-left px-3 py-2 hover:bg-primary/5 transition-colors flex items-center gap-2 border-b border-slate-50 last:border-b-0"
            >
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-sm text-slate-800">{user.name}</p>
                <p className="text-xs text-slate-500">{user.role} - {user.areas?.join(', ') || 'Sin área'}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Indicador de menciones */}
      {mentions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {mentions.map((m) => (
            <span 
              key={m.id}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700"
            >
              @{m.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
